import { MetricsLambdaFetch, awaitJSON } from '../../../../utils/fetch-utils';
import { MMM_MIN_OUTPUT_FILE_VERSION, MMM_RUN_BRANCH } from '../dtc-commerce-mmm-constants';

export interface MmmRun {
  timestamp: string;
  label: string;
  branch: string;
  // Always "2" or higher — the page filters to Meridian runs. Passed through to the data
  // loader's outputFileVersion param.
  outputFileVersion: string;
  clientFacing: boolean;
}

export interface MmmKpi {
  name: string;
  runs: MmmRun[];
}

export interface MmmDiscovery {
  kpis: MmmKpi[];
  truncated: boolean;
}

// One known run, shown in place of discovery. Coordinates are the run's S3 folder:
// mmm/{branch}/{client}/{kpi}/{timestamp}/.
export interface FixedMmmRun {
  client: string;
  branch: string;
  kpi: string;
  timestamp: string;
  outputFileVersion: string;
}

// Row shape from the metrics /dateOptions endpoint (mmm.getMMMDateOptions). model_type is
// filtered on server-side but not projected — adding it to the select would widen that query's
// DISTINCT key and change the row count for unfiltered callers.
interface DateOption {
  date: string;
  kpi: string;
  branch: string;
  client_facing?: boolean;
  output_file_version?: number | string | null;
}

const formatDateLabel = (timestamp: string): string => {
  const datePart = timestamp.split('T')[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return timestamp;
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatTimeLabel = (timestamp: string): string => {
  const timePart = timestamp.split('T')[1];
  if (!timePart) return '';
  const [hh = '0', mm = '00'] = timePart.split(':');
  const hour = Number(hh);
  if (Number.isNaN(hour)) return '';
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${mm} ${period}`;
};

/**
 * Label one KPI's runs: the date alone ("June 15, 2026"), plus the time when more than one of
 * them shares that date. Every MMM page's run selector labels its runs with this.
 */
export const formatRunLabels = (timestamps: string[]): string[] => {
  const dateCounts = new Map<string, number>();
  timestamps.forEach(timestamp => {
    const date = formatDateLabel(timestamp);
    dateCounts.set(date, (dateCounts.get(date) ?? 0) + 1);
  });
  return timestamps.map(timestamp => {
    const date = formatDateLabel(timestamp);
    const time = (dateCounts.get(date) ?? 0) > 1 ? formatTimeLabel(timestamp) : '';
    return time ? `${date}, ${time}` : date;
  });
};

const buildRuns = (rows: DateOption[]): MmmRun[] => {
  // Every run here is on DTC_BRANCH, so the label carries no branch tag — but two runs can
  // still land on the same day, hence the time disambiguation.
  const labels = formatRunLabels(rows.map(r => r.date));
  return rows.map((r, i) => ({
    timestamp: r.date,
    label: labels[i],
    branch: r.branch,
    outputFileVersion: String(r.output_file_version),
    clientFacing: Boolean(r.client_facing)
  }));
};

export const discoverMmmRuns = async (
  client: string,
  isInternal: boolean,
  modelType: string
): Promise<MmmDiscovery> => {
  if (!client) return { kpis: [], truncated: false };

  // The metrics lambda applies these in SQL. It rejects an unrecognized modelType rather than
  // returning everything, so a contract drift surfaces as an error instead of as another
  // model's runs rendering here.
  const res = await MetricsLambdaFetch('/dateOptions', {
    params: {
      company: client,
      modelType,
      branch: MMM_RUN_BRANCH,
      minOutputFileVersion: String(MMM_MIN_OUTPUT_FILE_VERSION)
    }
  });
  const rows = await awaitJSON<DateOption[]>(res);

  // Unreleased runs are internal-only, matching how the rMMM page gates its archive dropdown.
  const visibleRows = isInternal ? rows : rows.filter(r => r.client_facing);

  const grouped = new Map<string, DateOption[]>();
  visibleRows.forEach(r => {
    if (!r.kpi || !r.date) return;
    const list = grouped.get(r.kpi) ?? [];
    list.push(r);
    grouped.set(r.kpi, list);
  });

  const kpis: MmmKpi[] = Array.from(grouped.entries())
    .map(([name, kpiRows]) => ({
      name,
      runs: buildRuns(kpiRows.slice().sort((a, b) => b.date.localeCompare(a.date)))
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { kpis, truncated: false };
};

/**
 * A fixed run shaped as a discovery result, so the page treats it like any discovered run. It
 * is never registered, so it counts as unreleased: internal users only, and only on its own
 * client's page — another client's URL must not render this client's data.
 */
export const fixedRunDiscovery = (
  run: FixedMmmRun,
  client: string,
  isInternal: boolean
): MmmDiscovery => {
  if (!isInternal || client !== run.client) return { kpis: [], truncated: false };
  const runs = buildRuns([
    {
      date: run.timestamp,
      kpi: run.kpi,
      branch: run.branch,
      client_facing: false,
      output_file_version: run.outputFileVersion
    }
  ]);
  return { kpis: [{ name: run.kpi, runs }], truncated: false };
};

// The latest run across every KPI and both visibility groups — the one the run picker badges
// "Newest". Timestamps are ISO strings, so they sort lexically.
export const getNewestRunTimestamp = (kpis: MmmKpi[]): string | null =>
  kpis
    .flatMap(k => k.runs.map(r => r.timestamp))
    .reduce<string | null>((newest, ts) => (newest === null || ts > newest ? ts : newest), null);

export const runKey = (kpi: string, timestamp: string) => `${kpi}|${timestamp}`;

// Overlay optimistic client-facing flips (keyed by runKey) onto discovery's KPIs.
export const applyClientFacingOverrides = (
  kpis: MmmKpi[],
  overrides: Record<string, boolean>
): MmmKpi[] =>
  kpis.map(kpi => ({
    ...kpi,
    runs: kpi.runs.map(run => ({
      ...run,
      clientFacing: overrides[runKey(kpi.name, run.timestamp)] ?? run.clientFacing
    }))
  }));
